import React, { useEffect, useRef, useState } from "react";
import { useBlocker, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  composePlanche,
  getClient,
  getConversation,
  getJob,
  sendMessage,
} from "../../api/bd";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import HelpLink, { GUIDE_URLS } from "@/components/HelpLink";
import BDChapterPills from "./BDChapterPills";
import BDPhotosPanel from "./BDPhotosPanel";
import BDVersionsPanel from "./BDVersionsPanel";
import ImageMessage from "./ImageMessage";
import ModerationReformulationCard from "./ModerationReformulationCard";
import {
  chapterIndexFromImageUrl,
  extractTextAndImagesFromMessage,
} from "./messageImages";

// Borne de sécurité côté front : au-delà, on arrête de tourner indéfiniment sur
// un job mort (le backend timeoute à 10 min ; on laisse une marge). Revue #14.
const MAX_GENERATION_MS = 12 * 60 * 1000;
const AMBIANCE_LABELS = {
  lumineux_chaleureux: "Lumineux & chaleureux",
  doux_pastel: "Doux / pastel",
  naturel_equilibre: "Naturel / équilibré",
  contraste_vif: "Contrasté / vif",
};

// started_at est sérialisé en UTC *naïf* par le backend (datetime.utcnow(), sans
// 'Z'). new Date(...) l'interpréterait en heure locale → elapsed décalé du
// fuseau. On force l'UTC en ajoutant 'Z' s'il n'y a pas déjà de timezone. Revue #15.
function parseStartedAtMs(s) {
  if (!s || typeof s !== "string") return null;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  const ms = Date.parse(hasTz ? s : s + "Z");
  return Number.isNaN(ms) ? null : ms;
}

export default function BDChat({ token }) {
  const { clientId } = useParams();
  const [meta, setMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [composing, setComposing] = useState(false);
  const [elapsed, setElapsed] = useState(0); // secondes écoulées de génération
  const [activeChapter, setActiveChapter] = useState(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const startRef = useRef(null);
  const blocker = useBlocker(generating);

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("Une génération est en cours. Quitter quand même ?")) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  const refresh = async () => {
    const [m, c] = await Promise.all([getClient(token, clientId), getConversation(token, clientId)]);
    setMeta(m);
    setMessages(c.messages || []);
  };

  const stopTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  // Démarre le suivi d'une génération en cours : chrono + polling du statut.
  // Idempotent : pollRef sert de garde anti-double-démarrage.
  // startedAtMs (optionnel) : timestamp de début réel quand on REPREND une
  // génération déjà lancée (reload) — sinon le chrono repartirait à 0:00.
  const beginGenerating = (startedAtMs = null) => {
    if (pollRef.current) return;
    setGenerating(true);
    startRef.current = startedAtMs || Date.now();
    setElapsed(Math.max(0, Math.round((Date.now() - startRef.current) / 1000)));
    tickRef.current = setInterval(() => {
      const sec = Math.round((Date.now() - startRef.current) / 1000);
      setElapsed(sec);
      // Borne de sécurité : on n'attend pas indéfiniment un job mort (#14).
      if (sec * 1000 >= MAX_GENERATION_MS) {
        stopTimers();
        setGenerating(false);
        toast.error(
          "La génération semble bloquée. Recharge la page ou réessaie dans un instant."
        );
      }
    }, 1000);
    // B2 : compteur d'échecs de polling consécutifs. Après POLL_FAIL_MAX échecs
    // (~20 s à 4 s/poll), on arrête le suivi et on informe l'utilisateur plutôt
    // que de laisser le décompte disparaître silencieusement.
    const POLL_FAIL_MAX = 5;
    let consecutiveFails = 0;
    pollRef.current = setInterval(async () => {
      try {
        const job = await getJob(token, clientId);
        consecutiveFails = 0; // réinitialise sur tout succès réseau
        if (job.status === "done") {
          stopTimers();
          setGenerating(false);
          await refresh();
          toast.success("Planche générée ✅");
        } else if (job.status === "error") {
          stopTimers();
          setGenerating(false);
          toast.error(job.error || "La génération a échoué.");
          refresh().catch(() => {});
        } else if (job.status === "moderation_blocked") {
          stopTimers();
          setGenerating(false);
          await refresh();
          toast("Scène bloquée par la modération — reformulation proposée, à valider.");
        }
      } catch (_e) {
        // Erreur réseau transitoire : on incrémente le compteur plutôt que
        // d'ignorer silencieusement.
        consecutiveFails += 1;
        if (consecutiveFails >= POLL_FAIL_MAX) {
          stopTimers();
          setGenerating(false);
          toast.error(
            "Connexion instable — on ne peut plus suivre la génération. " +
            "Recharge la page pour voir si la planche est prête."
          );
        }
      }
    }, 4000);
  };

  // Mount : charge la conv + reprend le suivi si une génération tourne déjà
  // (ex : l'utilisatrice a rechargé la page en pleine génération).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh().catch(console.error);
      try {
        const job = await getJob(token, clientId);
        if (!cancelled && job.status === "running") {
          beginGenerating(parseStartedAtMs(job.started_at));
        }
      } catch (_e) { /* pas de job */ }
    })();
    return () => { cancelled = true; stopTimers(); };
  }, [clientId, token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, generating]);

  const onSend = async () => {
    if (!input.trim() || generating) return;
    const userMsg = input;
    setInput("");
    // Optimistic : le message utilisateur s'affiche tout de suite.
    setMessages((prev) => [
      ...prev,
      { role: "user", content: [{ type: "text", text: userMsg }] },
    ]);
    try {
      await sendMessage(token, clientId, userMsg); // → { status: "running" }
      beginGenerating();
    } catch (e) {
      if (e?.response?.status === 409) {
        // Une génération tourne déjà : on bascule simplement en suivi
        // (le verrou backend a empêché un 2e déclenchement = pas de surcoût).
        // On récupère son started_at pour afficher le chrono au bon temps (#15).
        try {
          const job = await getJob(token, clientId);
          beginGenerating(parseStartedAtMs(job?.started_at));
        } catch (_e2) {
          beginGenerating();
        }
      } else {
        toast.error(e?.response?.data?.detail || e.message);
        refresh().catch(() => {});
      }
    }
  };

  const onComposePlanche = async () => {
    if (composing) return;
    setComposing(true);
    try {
      await composePlanche(token, clientId);
      await refresh();
      toast.success("Planche complète composée.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setComposing(false);
    }
  };

  if (!meta) return <div className="p-6">Chargement…</div>;

  const styleLabel = meta.character?.style_choisi || "(style non configuré)";
  const ambianceKey = meta.character?.ambiance_choisie;
  const ambianceLabel = ambianceKey ? AMBIANCE_LABELS[ambianceKey] || ambianceKey : null;
  const activeChapterMeta = (meta.chapters || []).find((c) => c.chapter_index === activeChapter);
  const activeChapterLabel = activeChapter === null
    ? null
    : activeChapter === 6
      ? "Planche complète"
      : `Chapitre ${activeChapter}`;
  const activeChapterValidated = Boolean(
    activeChapterMeta?.status === "validated" ||
    activeChapterMeta?.validated ||
    activeChapterMeta?.validated_at
  );
  const activeChapterValidatedOutdated = Boolean(activeChapterMeta?.validated_outdated);
  const activePlancheExists = Boolean(
    activeChapter === 6 &&
    activeChapterMeta &&
    activeChapterMeta.status !== "pending"
  );
  const missingChapterIndexes = [1, 2, 3, 4, 5].filter((chapterIndex) => {
    const chapter = (meta.chapters || []).find(
      (candidate) => candidate.chapter_index === chapterIndex
    );
    return !(
      chapter?.status === "validated" ||
      chapter?.validated ||
      chapter?.validated_at
    );
  });
  const missingChaptersText = missingChapterIndexes.length === 0
    ? null
    : missingChapterIndexes.length === 1
      ? `Chapitre ${missingChapterIndexes[0]} à valider d'abord`
      : `Chapitres ${missingChapterIndexes.slice(0, -1).join(", ")} et ${
          missingChapterIndexes[missingChapterIndexes.length - 1]
        } à valider d'abord`;
  const moderationChapters = (meta.chapters || []).filter((chapter) => (
    chapter.proposed_reformulation &&
    activeChapter === null
  ));

  return (
    <div className="flex h-[calc(100vh-var(--shell-h))] flex-col">
      <div className="flex items-center justify-between gap-4 border-b bg-white px-6 py-2 text-xs text-gray-500">
        <p>
          Style : {styleLabel}
          {ambianceLabel && <> · Ambiance : {ambianceLabel}</>}
        </p>
        <HelpLink url={GUIDE_URLS.bd} />
      </div>

      <BDPhotosPanel
        token={token}
        clientId={clientId}
        photoRefs={meta.character?.photos_refs}
        portraitsByBracket={meta.character?.portraits_by_bracket}
        calibrationPending={meta.character?.calibration_pending}
        chapters={meta.chapters || []}
        onPhotoRefsChange={(photoRefs) => {
          setMeta((current) => {
            if (!current?.character) return current;
            return {
              ...current,
              character: {
                ...current.character,
                photos_refs: photoRefs,
              },
            };
          });
        }}
        onPortraitsChange={(portraits) => {
          setMeta((current) => {
            if (!current?.character) return current;
            return {
              ...current,
              character: {
                ...current.character,
                portraits_by_bracket: portraits,
              },
            };
          });
        }}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar 15% */}
        <aside className="w-48 border-r bg-gray-50 p-3 overflow-y-auto">
          <p className="text-xs font-bold uppercase text-gray-500 mb-2">Chapitres</p>
          <BDChapterPills
            chapters={meta.chapters || []}
            activeIndex={activeChapter}
            onSelect={(i) => setActiveChapter(activeChapter === i ? null : i)}
          />
        </aside>

        {/* Chat 85% */}
        <main className="flex-1 flex flex-col bg-white overflow-hidden">
          {activeChapterLabel && (
            <div className="border-b border-orange-200 bg-orange-50 px-6 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase text-orange-700">Chapitre courant</p>
                <p className="font-semibold text-orange-950">{activeChapterLabel}</p>
              </div>
              <div className="flex items-center justify-end gap-3">
                {activeChapter === 6 && (
                  <>
                    <Button
                      data-testid="bd-compose-planche-button"
                      size="sm"
                      onClick={onComposePlanche}
                      disabled={composing || missingChapterIndexes.length > 0}
                      className="bg-orange-600 text-white hover:bg-orange-700"
                    >
                      {composing ? "Composition…" : "Composer la planche complète"}
                    </Button>
                    {missingChaptersText && (
                      <span className="text-sm text-orange-800">{missingChaptersText}</span>
                    )}
                  </>
                )}
                {activeChapterValidatedOutdated ? (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                    ⚠ Version validée dépassée
                  </Badge>
                ) : activeChapterValidated ? (
                  <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                    ✓ Validée
                  </Badge>
                ) : activePlancheExists && (
                  // Une consigne à lire, pas un statut : les pastilles voisines
                  // tiennent en deux mots, celle-ci tient en deux phrases.
                  <p
                    data-testid="bd-planche-a-valider"
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-900"
                  >
                    <strong>Planche prête à valider.</strong>{" "}
                    Elle n'apparaîtra sur le mini-site qu'après validation.
                  </p>
                )}
              </div>
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
            {moderationChapters.map((chapter) => (
              <ModerationReformulationCard
                key={`moderation-${chapter.chapter_index}`}
                token={token}
                clientId={clientId}
                chapterIndex={chapter.chapter_index}
                proposedReformulation={chapter.proposed_reformulation}
                onRefresh={refresh}
                onRegen={beginGenerating}
              />
            ))}
            {messages.length === 0 && moderationChapters.length === 0 && (
              <p className="text-center text-gray-500 mt-12">
                Tape ton premier message pour démarrer (ex: « Génère la BD du chapitre 3 »).
              </p>
            )}
            {messages.map((msg, idx) => {
              const isOptimistic = typeof msg.content === "object" && Array.isArray(msg.content)
                && msg.content[0]?.type === "text" && msg.role === "user";
              let text = "", imageUrls = [];
              if (isOptimistic && msg.content[0]?.text) {
                text = msg.content[0].text;
              } else {
                const extracted = extractTextAndImagesFromMessage(msg);
                text = extracted.text;
                imageUrls = extracted.imageUrls;
              }
              const filteredImageUrls = activeChapter === null
                ? imageUrls
                : imageUrls.filter((url) => chapterIndexFromImageUrl(url) === activeChapter);
              const isUser = msg.role === "user";
              if (!text && filteredImageUrls.length === 0) return null;
              return (
                <div key={idx} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-2xl px-4 py-2 rounded-lg ${
                    isUser ? "bg-orange-100" : "bg-gray-100"
                  }`}>
                    {text && <p className="whitespace-pre-wrap">{text}</p>}
                    {filteredImageUrls.map((url) => (
                      <ImageMessage
                        key={url}
                        token={token}
                        imageUrl={url}
                        onRefresh={refresh}
                        onRegen={beginGenerating}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {generating && (
              <div className="flex justify-start">
                <div className="max-w-2xl px-4 py-3 rounded-lg bg-gray-100 text-gray-700 flex flex-col gap-1">
                  <div className="flex items-center gap-2 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                    Génération en cours…
                    <span className="text-gray-500 font-normal tabular-nums">
                      {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    Extraction du chapitre puis dessin de la planche : compte
                    en général <strong>2 à 3 minutes</strong> (jusqu'à 5 min les
                    jours chargés). Inutile de relancer — ça continue tout seul,
                    la planche s'affichera ici dès qu'elle est prête.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-4">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && onSend()}
                placeholder={generating ? "Génération en cours…" : "Ton message…"}
                disabled={generating}
                className="flex-1 border rounded px-3 py-2"
              />
              <button
                onClick={onSend}
                disabled={generating || !input.trim()}
                className="bg-orange-600 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {generating ? "Patiente…" : "Envoyer"}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Tape <code className="bg-gray-100 px-1">/step</code> en début de message pour valider scène par scène.
            </p>
          </div>
        </main>

        {/* Panel versions à droite (conditionnel) */}
        {activeChapter !== null && (
          <BDVersionsPanel
            token={token}
            clientId={clientId}
            chapterIndex={activeChapter}
            chapterMeta={activeChapterMeta}
            onClose={() => setActiveChapter(null)}
            onChanged={refresh}
            onRegen={beginGenerating}
          />
        )}
      </div>
    </div>
  );
}
